import { type PayloadAction, createSlice } from "@reduxjs/toolkit";

export type MediaType = "image" | "video" | "audio" | "file";
export type ViewMode = "grid" | "list";

export interface UploadQueueItem {
  id: string; // Unique ID for the upload
  file: File;
  progress: number;
  status: "pending" | "uploading" | "success" | "error";
  error?: string;
}

export interface FolderPathItem {
  id: string | null;
  name: string;
}

export interface MediaState {
  selectedFolderId: string | null;
  selectedMediaType: MediaType;
  viewMode: ViewMode;
  uploadQueue: UploadQueueItem[];
  folderPath: FolderPathItem[];
}

const initialState: MediaState = {
  selectedFolderId: null,
  selectedMediaType: "image",
  viewMode: "grid",
  uploadQueue: [],
  folderPath: [],
};

const mediaSlice = createSlice({
  name: "media",
  initialState,
  reducers: {
    setSelectedFolder: (state, action: PayloadAction<string | null>) => {
      state.selectedFolderId = action.payload;
    },
    navigateToFolder: (
      state,
      action: PayloadAction<{
        folderId: string | null;
        folderName: string;
        parentPath?: FolderPathItem[];
      }>
    ) => {
      state.selectedFolderId = action.payload.folderId;
      if (action.payload.folderId === null) {
        // Navigating to root
        state.folderPath = [];
      } else if (action.payload.parentPath) {
        // Navigating from breadcrumb or with known path
        state.folderPath = [
          ...action.payload.parentPath,
          { id: action.payload.folderId, name: action.payload.folderName },
        ];
      } else {
        // Navigating to subfolder from current location
        state.folderPath = [
          ...state.folderPath,
          { id: action.payload.folderId, name: action.payload.folderName },
        ];
      }
    },
    setFolderPath: (state, action: PayloadAction<FolderPathItem[]>) => {
      state.folderPath = action.payload;
    },
    setMediaType: (state, action: PayloadAction<MediaType>) => {
      state.selectedMediaType = action.payload;
      // Reset selected folder and path when changing media type
      state.selectedFolderId = null;
      state.folderPath = [];
    },
    setViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.viewMode = action.payload;
    },
    addToUploadQueue: (state, action: PayloadAction<{ id: string; file: File }>) => {
      state.uploadQueue.push({
        id: action.payload.id,
        file: action.payload.file,
        progress: 0,
        status: "pending",
      });
    },
    updateUploadProgress: (state, action: PayloadAction<{ id: string; progress: number }>) => {
      const item = state.uploadQueue.find((i) => i.id === action.payload.id);
      if (item) {
        item.progress = action.payload.progress;
        item.status = "uploading";
      }
    },
    setUploadSuccess: (state, action: PayloadAction<string>) => {
      const item = state.uploadQueue.find((i) => i.id === action.payload);
      if (item) {
        item.status = "success";
        item.progress = 100;
      }
    },
    setUploadError: (state, action: PayloadAction<{ id: string; error: string }>) => {
      const item = state.uploadQueue.find((i) => i.id === action.payload.id);
      if (item) {
        item.status = "error";
        item.error = action.payload.error;
      }
    },
    removeFromUploadQueue: (state, action: PayloadAction<string>) => {
      state.uploadQueue = state.uploadQueue.filter((i) => i.id !== action.payload);
    },
    clearUploadQueue: (state) => {
      state.uploadQueue = [];
    },
  },
});

export const {
  setSelectedFolder,
  navigateToFolder,
  setFolderPath,
  setMediaType,
  setViewMode,
  addToUploadQueue,
  updateUploadProgress,
  setUploadSuccess,
  setUploadError,
  removeFromUploadQueue,
  clearUploadQueue,
} = mediaSlice.actions;

export default mediaSlice.reducer;
